// src/encounter/medication-dose-dialog.tsx
// Step 2 of the A/P medication attach — the full Flutter-parity dose form (PROD-1340, porting
// new_medication_add_update_widget.dart). Selection-based fields (Route / Dose unit / Dispense unit
// / Frequency are DS Select dropdowns, NOT free text), PRE-FILLED from the picked medication
// (route + unitOfMeasurement ride the catalog row), with the frequency model driving the conditional
// fields + the wire occurenceRequest[]. Owns its master-list hooks (like the lab/imaging/procedure
// add dialogs). On confirm it emits a structured CodedOrderPayload via medicationResultToOrder.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  NumberSelector,
  Segmented,
  Switch,
  Modal,
  Button,
  TextField,
  TextArea,
  Select,
  DatePicker,
  Grid,
  HelperText,
  type SelectOption,
} from '@scripsteam/scrips-signal-ds';
import { Trash2 } from 'lucide-react';
import { medicationResultToOrder, type MedicationOrderResult, type MedicationDose } from './lib/to-medication-order';
import { DiagnosisMultiSelect, type DiagnosisOption } from './diagnosis-multi-select';
import type { MedicationPick } from './lib/to-medication-result';
import { keepSourceSystem } from './lib/source-system';
import {
  MED_FREQUENCY,
  MED_FREQUENCY_OPTIONS,
  MED_FREQUENCY_ALL,
  WEEKDAYS,
  WEEKDAY_SHORT,
  MED_PRIORITIES,
  DURATION_UNITS,
  frequencyIsScheduled,
  type MedFrequency,
} from './lib/medication-frequency';
import { useMedicationUnits, useMedicationRoutes } from './services/use-medication-masters';
import { useMedicationValueSet } from './services/use-medication-valueset-search';
import { useMedicationInteractionCheck } from './services/use-medication-interaction-check';
import type { DrugInteractionDiagnosisInput, DrugInteractionMedInput } from './lib/to-drug-interaction-input';
import { DrugInteractionWarning } from './drug-interaction-warning';
import { DrugInteractionAcknowledgeDialog } from './drug-interaction-acknowledge-dialog';
import { NoMedicationChecksNotice } from './no-medication-checks-notice';
import { AP_DIALOG_LABELS } from './ap-dialog-label-style';

/** The confirm payload, widened with the diagnoses the clinician linked (PROD-1801). */
export interface MedicationConfirm extends MedicationOrderResult {
  /** Every linked diagnosis, in canvas order — dxIds[0] is the nesting parent. */
  dxIds: string[];
}

export interface MedicationDoseDialogProps {
  /**
   * The encounter's diagnoses for the DIAGNOSIS (n) * block (PROD-1801). Named `dxOptions`, not
   * `diagnoses`, because this dialog already has a `diagnoses` prop carrying drug-interaction
   * context (a different shape for a different purpose) — see below.
   */
  dxOptions: DiagnosisOption[];
  /** Pre-ticked diagnoses — the canvas seeds the Dx the picker was opened from. */
  initialDxIds?: readonly string[];
  /** The medication picked in step 1 (carries route[] + unitOfMeasurement for pre-fill). */
  medication: MedicationPick;
  /** Stable suffix folded into the generated orderId (the host increments per attachment). */
  idSuffix?: string | number;
  /**
   * Editing an existing order (PROD-1433): seed every dose field from the saved order so the dialog
   * opens pre-filled (Flutter's `originalData`). When present, route/uom are taken from the seed (the
   * med already carries them), so the by-code lookup does not override the saved values.
   */
  initialDose?: MedicationDose;
  /** Editing an existing order — switches the title/confirm copy to "Edit". */
  editing?: boolean;
  onConfirm: (order: MedicationConfirm) => void;
  /** Back to search (clears the pick) / cancel. */
  onCancel: () => void;
  /**
   * Destructive DELETE, bottom-right — the red trash glyph in Figma `1736:158994`. Editing only (the
   * host passes its existing order-removal callback); omitted, nothing renders.
   */
  onDelete?: () => void;

  // ── Inline drug-interaction check (PROD parity — Flutter getDrugInteractions) ────────────────
  // When patientId is supplied the dialog runs the interaction engine for the picked med and shows
  // an inline warning under the med header (drug-drug / drug-allergy / drug-disease). Optional so
  // hosts without patient context (Storybook, tests) simply render no warning.
  /** Patient the Rx is for (Flutter `subject`). Enables the interaction check. */
  patientId?: string;
  /** Prescriber (Flutter `performer`). */
  providerId?: string;
  /** Working diagnoses providing drug-disease context (Flutter `selectedDiagnosis`). */
  diagnoses?: DrugInteractionDiagnosisInput[];
  /** Other medications already on the plan, so drug-drug pairs within the draft are checked too. */
  existingMedications?: DrugInteractionMedInput[];

  /**
   * AUTHORING an order set (PROD-2714, Settings › Order sets): the same form with no patient and no
   * encounter. The fields that belong to one visit are not shown and not required: the DIAGNOSIS
   * block (a set carries its own diagnoses), the start date, weight and the pharmacy notes (the
   * Save-as board's "Not kept: the patient, dates, notes to the lab, order and lot numbers"). Pass no
   * `patientId`: the interaction check runs when the set is applied, not here.
   */
  authoring?: boolean;
}

/**
 * Text field -> integer. Module scope, because the dispense initialiser needs it and used to be
 * declared above it inside the component body — a temporal-dead-zone error the moment the
 * initialiser started reading seeded numbers.
 */
const num = (s: string) => Number.parseInt(s, 10);

/** Number → text-field string ('' when absent), for seeding the numeric inputs from a saved dose. */
function numStr(n: number | undefined): string {
  return typeof n === 'number' ? String(n) : '';
}

/**
 * A saved value if there is one, otherwise FLUTTER'S DEFAULT for a new prescription
 * (new_medication_add_update_widget.dart:73-81 + setInitialDispense():213-219 —
 * quantity 1.0 · timesADay 1 · everyNumberOfDay 1 · duration 1 · refills 0 · dispense 0).
 *
 * This closes a real defect, not just a convenience gap. The counters RENDER `num(x) || 1`, so an
 * untouched "Times a day" showed **1** — but the state stayed '' and `num('')` is NaN, which
 * `Number.isFinite` rejects, so the save sent `undefined`. The clinician saw a dose the record never
 * received. Seeding the state makes the displayed value the value that persists.
 *
 * `numStr(0)` is '0', which is a truthy string, so a legitimately-saved 0 is preserved — only a
 * genuinely absent value falls through to the default.
 */
function numStrOr(n: number | undefined, fallback: string): string {
  return numStr(n) || fallback;
}

/** Today at local midnight — Flutter seeds the start date with `DateTime.now()` (NMAUW:80, :218). */
function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Flutter's duration-unit multiplier — day 1 · wk 7 · mo 30 (NMAUW setFrequencyEveryDayDispense:247-255). */
const DURATION_UNIT_DAYS: Record<string, number> = { day: 1, wk: 7, mo: 30 };

/** Text field → INTEGER (NaN when blank/non-numeric). Module-level twin of the in-component `num`,
 *  which is declared further down the body than the dispense effect that needs it.
 *  The two DOUBLE fields — units-per-intake and weight — use {@link parseDecimal} instead. */
function parseNum(s: string): number {
  return Number.parseInt(s, 10);
}

/**
 * Text field → number, FRACTIONS PRESERVED. Flutter holds two of these as doubles and reads them with
 * `double.tryParse`: `quantity` (units per intake, stepped by **0.25** —
 * new_medication_add_update_widget.dart:758,765) and `weight` (:947).
 *
 * `parseInt` on either is a live corruption path, not a hypothetical:
 *   • LOAD — a Flutter-saved `quantity: 0.5` parsed to 0, rendered as 1 via the old `|| 1` fallback,
 *     and then SAVED BACK AS 0. Half-tablet and half-mL dosing is routine, so that is a real
 *     prescription being silently rewritten.
 *   • TYPED — `weight` is a decimal-inputMode TextField, so 72.5 was accepted from the clinician and
 *     persisted as 72.
 * Same defect class as the seeded-counter bug: what is shown is not what is stored.
 */
function parseDecimal(s: string): number {
  return Number.parseFloat(s);
}

/** Flutter's units-per-intake stepper moves in QUARTERS from 0 (NMAUW:758 `quantity += 0.25`, :751
 *  `max(0, …)`) — which is why the counter must be a decimal control, not an integer one. */
const QUANTITY_STEP = 0.25;

/** The schedule inputs the dispense derivation reads. */
interface DispenseInputs {
  frequency: MedFrequency;
  timesADay: number;
  quantity: number;
  duration: number;
  durationUnit: string;
  daysInterval: number;
  specificDaysCount: number;
}

/**
 * DISPENSE IS DERIVED FROM THE SCHEDULE, per frequency — ported from Flutter
 * (new_medication_add_update_widget.dart):
 *
 *   Now           (setInitialDispense:224)           → 1
 *   As needed     (setInitialDispense:229)           → 0
 *   Every day     (setFrequencyEveryDayDispense)     → ceil(t · d · q) · totalDuration
 *   Days interval (setFrequencyDaysIntervalDispense) → ceil(ceil(t · d · q · totalDuration) / everyN)
 *   Specific days (setFrequencySpecificDaysDispense) → ceil(ceil(t · days · q · d · totalDuration) / 7)
 *
 * The NESTED ceil in the last two is Flutter's own, not a transcription artefact — it rounds the total
 * up BEFORE dividing, then rounds the quotient up again. Reproduced exactly, because a dispensed
 * quantity that disagrees with the prescription is a pharmacy problem.
 *
 * Dispense is therefore never a flat default: the save is gated on `dispense > 0`, so seeding a literal
 * 0 would leave a new prescription permanently unsaveable.
 *
 * `null` = "cannot derive — leave the field alone" (a non-numeric input mid-edit).
 *
 * NOTE on quantity: `num()` is parseInt, so a fractional units-per-intake narrows to an integer here
 * where Flutter keeps a double. Pre-existing and unchanged by this port; only reachable if the counter
 * ever admits decimals. Flagged rather than silently changed alongside a behaviour port.
 */
function derivedDispense(i: DispenseInputs): number | null {
  if (i.frequency === MED_FREQUENCY.now) return 1;
  if (i.frequency === MED_FREQUENCY.asNeeded) return 0;

  const total = DURATION_UNIT_DAYS[i.durationUnit] ?? 1;
  const { timesADay: t, quantity: q, duration: d } = i;
  if (![t, q, d].every((n) => Number.isFinite(n))) return null;

  if (i.frequency === MED_FREQUENCY.everyDay) return Math.ceil(t * d * q) * total;
  if (i.frequency === MED_FREQUENCY.daysInterval) {
    if (!Number.isFinite(i.daysInterval) || i.daysInterval <= 0) return null;
    return Math.ceil(Math.ceil(t * d * q * total) / i.daysInterval);
  }
  if (i.frequency === MED_FREQUENCY.specificDays) {
    return Math.ceil(Math.ceil(t * i.specificDaysCount * q * d * total) / 7);
  }
  return null;
}

/**
 * FLUTTER'S FIELD HEIGHT IS 36px — `FieldAndLabel` defaults to `this.height = 36` and renders
 * `height: 36.0` (scrips_core .../field_and_label.dart:237, :1381-1382, :1444).
 *
 * That resolves a size question we went round three times. The DS compact family ALREADY matches
 * Flutter: Select, TextField and NumberSelector are all 36. The only outlier is the DatePicker trigger
 * at `h-8` (32px) — so the fix is to RAISE the DatePicker, not to lower everything else to it. Which
 * also fixes the Start-date-vs-Duration misalignment, because that gap WAS the 4px.
 *
 * Done LOCALLY and scoped, on Andrew's explicit instruction (31 Jul: "ignore ds in the sizes",
 * "take the controls from ds but the sizes from flutter") after I twice proposed a DS PR instead. It is
 * scoped to this dialog so it cannot leak to other surfaces, and it targets the trigger's own height
 * class rather than every button. The DS-side fix — bringing DatePicker into the compact family — is
 * still the right long-term home for it, and is recorded on A/P-15.
 *
 * Label treatment has MOVED OUT to ap-dialog-label-style (Andrew, 2 Aug) — it is now one shared
 * uppercase rule across every A&P dialog rather than a colour patch here. The rule this replaced only
 * recoloured the <label> element, which silently missed Select and DatePicker: FieldShell puts their
 * text in an inner <span>. It looked right purely because that span already used the same colour.
 */
const FLUTTER_36 = 'ap-med-flutter-sizing';
const FLUTTER_36_CSS = `
.${FLUTTER_36} button.h-8 { height: 36px; }

/*
  ONE CONTROL WIDTH for the whole form.
  The counters are a natural 196px; the selects and the date picker filled their 290px grid column, so
  every counter row ended 94px short of the row above it and nothing lined up down the form.
  FIRST ATTEMPT stretched the counters to 290 — that matched the edges and looked worse, because the
  value well grew with them and left "1" adrift in a wide grey box. Reverted.
  This does the opposite: brings the wide controls DOWN to the counter's width, so every control is the
  same size with the same left and right edge, and nothing is stretched out of proportion.
*/
.${FLUTTER_36} [role='combobox'],
.${FLUTTER_36} button.h-8 { max-width: 196px; }
`;

const BODY = 'font-sans flex flex-col gap-3';
/**
 * Field-label treatment for the controls that carry no `label` prop — the MEDICATIONS header, the
 * Segmented frequency row, the weekday chips, the substitution switch. ONE style, because the live
 * dialog gives every field label the same uppercase treatment; an earlier draft also had a 13px muted
 * `SEGMENTED_LABEL`, which made "Frequency" and "On which days" the only sentence-case labels on the
 * form. (The DS-labelled controls — TextField/Select/DatePicker/TextArea — still render THEIR labels
 * sentence-case. That gap is the DS's to close, not something to paper over here with a local
 * override; flagged in the PR.)
 *
 * NOTE: AP_FIELD_LABEL lives in ap-dialog-label-style.tsx (out of scope for this pass), so its
 * literal values (12/700/uppercase/0.01em/--color-text-hint) are reproduced here as the equivalent
 * class string rather than imported as a style object.
 */
const MED_FIELD_LABEL =
  'block text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint mb-1';
const MED_NAME = 'text-size-body-15 leading-reading text-text-primary';
const MED_CODE = 'text-size-sm leading-reading text-text-muted';
/** EVERY 'NUMBER' OF DAYS — quoted exactly as the live dialog labels it. */
const DAYS_INTERVAL_LABEL = "Every 'number' of days";
const DURATION_LINKS = 'inline-flex gap-2';
const DURATION_LINK_BASE =
  'border-0 bg-transparent p-0 cursor-pointer font-inherit text-size-sm font-medium';
const DURATION_LINK_ON = `${DURATION_LINK_BASE} text-brand-primary`;
const DURATION_LINK_OFF = `${DURATION_LINK_BASE} text-text-hint`;
const SUBSTITUTION_ROW = 'flex items-center justify-between gap-3';
// fontSize 14: no DS token (LIST — arbitrary px).
const SUBSTITUTION_LABEL = 'text-size-subtitle text-text-primary';
const FREQUENCY_ROW = 'col-span-full min-w-0 overflow-x-auto';
// `auto-fit` + minmax rather than a rigid 1fr 1fr: the NumberSelector is a fixed 196px block (the
/**
 * The contradiction banner. Deliberately loud and deliberately NOT dismissible: it is shown only
 * when the transcript and the model disagree about a dosing schedule, which is the one thing on
 * this form that can injure someone. `role="alert"` so it is announced rather than merely drawn.
 */
// `--color-status-warning-border` is not a real DS token — it has no definition anywhere in
// tokens.css, so this custom property was always undefined and the browser fell through to the
// literal fallback on every render. The fallback was an EXACT match for `--color-status-warning`
// (tokens.css:77 / DS `--color-yellow-500`), so the fix is pixel-identical: reference the real
// token directly instead of a phantom one with a hex safety net.
//
// `--color-status-warning-surface` is likewise not a real token, so that declaration was also
// running on its hex fallback. Unlike the border there is NO exact match for that fallback:
// the candidates are `--color-status-warning-bg` and `--color-yellow-100`, and neither is equal.
//
// Resolved on Ahmed's 2026-09-22 ruling ("map to the nearest DS token") in favour of
// `--color-status-warning-bg` — the SEMANTIC match, since this is the surface of a warning
// banner whose border is already `--color-status-warning`. `--color-yellow-100` is numerically
// nearer but is a raw palette step, and binding a status surface to a palette ramp is how a
// status colour silently stops tracking its own token.
//
// ⚠️ THIS ONE MOVES PIXELS, unlike the border and text fixes. The background shifts by a
// small amount in the green and blue channels. That is a deliberate, ruled trade — recorded here
// so a future parity measurement finds the reason instead of reading it as drift.
//
// `--color-text-primary` IS a real token (tokens.css:88), so its former hex fallback was dead
// code carrying a value that did not match the token it never reaches. Dropped for the same
// pixel-identical reason as the border above.
//
// padding '10px 14px' / borderRadius 8 / fontSize 13 / lineHeight 1.45 → spacing steps 2.5 / 3.5,
// --radius-md, --text-sm and --leading-meta (1.45).
const CONTRADICTION_BANNER =
  'col-span-full py-2.5 px-4 rounded-md border border-solid border-status-warning bg-status-warning-bg text-text-primary text-size-sm leading-meta';

// An intrinsic grid rather than a rigid 1fr 1fr: the NumberSelector is a fixed 196px block (the
// Figma counter width), so a rigid two-column grid overflows the card at narrower widths. Caught by
// rendering it, not by the tests — they all passed. Every field pair is TWO cells, so the DS `Grid`
// (2 columns, a 216 floor, the 12px `3` gap) lays them out as the old auto-fit minmax(216px) did:
// side by side from 444px of content, stacked below.
function DoseGrid({ children }: { children: ReactNode }) {
  return (
    <Grid columns={2} minColWidth="var(--layout-dose-col)" gap="3" className="items-start">
      {children}
    </Grid>
  );
}
const DAYS = 'flex flex-wrap gap-3';
// Save + Cancel LEFT, delete far RIGHT — the footer both Figma frames draw, and the same layout
// OrderDialogShell uses, so all five order dialogs agree.
// FIXED FOOTER, as Flutter (Andrew, 1 Aug: "the user should not scroll to the end to close or save").
// base_ap_item_add_widget.dart:88 wraps the body in `Expanded(child: SingleChildScrollView(...))` and
// leaves the actions OUTSIDE the scroll area, so Save/Cancel are always reachable however long the
// form runs. Sticky rather than absolutely positioned: the row keeps its place in flow, so it cannot
// overlap the last field the way a fixed overlay would.
const ACTIONS =
  'sticky bottom-0 bg-surface-card pt-3 pb-1 border-t border-solid border-t-border-subtle z-1 flex items-center justify-between gap-2 mt-2';
const ACTIONS_MAIN = 'flex items-center gap-2';
/** Bare red trash, per the frames — see the same note in OrderDialogShell (a DS gap, flagged). */
/** The app icon button: a destructive icon is neutral, like every row bin (icon pass, 16 Oct; it was red at rest). */
const DELETE_BUTTON = 'icon-action';

/** Local-date → no-Z ISO (avoids the UTC day-shift a naive toISOString would cause). */
function toLocalDateIso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;
}

function toOptions(values: string[]): SelectOption[] {
  return values.map((v) => ({ label: v, value: v }));
}

/**
 * "Route and dispense are required." — build the required-field banner naming ONLY the
 * genuinely-missing fields, clearing each as it fills (PROD-1588: the old static group string
 * named every field in its group — including visibly-FILLED dose unit / dispense unit /
 * start date — whenever ANY one field in the group was empty).
 */
function requiredMessage(fields: string[], suffix = ''): string {
  const joined =
    fields.length === 1
      ? fields[0]
      : fields.length === 2
        ? `${fields[0]} and ${fields[1]}`
        : `${fields.slice(0, -1).join(', ')}, and ${fields[fields.length - 1]}`;
  const sentence = joined.charAt(0).toUpperCase() + joined.slice(1);
  // "Route is / Units per intake are" — a single field is singular unless its head noun is plural.
  const singular = fields.length === 1 && !fields[0].split(' ')[0].endsWith('s');
  return `${sentence} ${singular ? 'is' : 'are'} required${suffix}.`;
}

export function MedicationDoseDialog({ medication, dxOptions, initialDxIds, idSuffix = '0', initialDose, editing, onConfirm, onCancel, onDelete, patientId, providerId, diagnoses, existingMedications, authoring = false }: MedicationDoseDialogProps) {
  // ── Master lists (own them, like the other add dialogs). Route falls back to the global list
  //    only when the picked med carries no route[] of its own. ──
  const unitsQuery = useMedicationUnits();
  const routesQuery = useMedicationRoutes();

  // Flutter `fetchSelectedMedicationData`: a provider-catalog medication carries NO route/uom, so on
  // open we look them up BY CODE from the value-set endpoint (Master/SearchValueSets2?SearchFor=
  // Medications). Skip the fetch when the med already carries route + uom (e.g. an edit seeded from the
  // saved order — Flutter's `originalData` wins over the fetch).
  const needsLookup = !!medication.code && (!(medication.route?.length) || !medication.unitOfMeasurement);
  const valueSet = useMedicationValueSet(medication.code ?? '', needsLookup);
  const fetchedMed = useMemo(() => {
    const rows = valueSet.data ?? [];
    return rows.find((r) => r.code === medication.code) ?? rows[0];
  }, [valueSet.data, medication.code]);

  const routeOptions: SelectOption[] = useMemo(() => {
    const fromMed = (medication.route ?? []).map((r) => ({ label: r.display, value: r.code }));
    if (fromMed.length > 0) return fromMed;
    const fromFetch = (fetchedMed?.route ?? []).map((r) => ({ label: r.display, value: r.code }));
    if (fromFetch.length > 0) return fromFetch;
    return (routesQuery.data ?? [])
      .filter((r) => !!r.code)
      .map((r) => ({ label: r.display ?? (r.code as string), value: r.code as string }));
  }, [medication.route, fetchedMed, routesQuery.data]);

  // Graceful degradation: if a master list fails to load and we have no options to fall back on,
  // surface a Select error rather than a silently-empty dropdown (the required gate already blocks
  // a blank submit, but the user needs to know why it's empty). Routes usually ride the med, so
  // only error when both the med and the master list gave us nothing.
  const unitsError = unitsQuery.isError ? 'Couldn’t load units — retry shortly.' : undefined;
  const routeError = routeOptions.length === 0 && routesQuery.isError ? 'Couldn’t load routes — retry shortly.' : undefined;

  // ── Pre-fill from the picked medication (Flutter setRouteData): route = first, units = the med's UoM.
  //    A provider-catalog med starts blank here; the by-code lookup effect below fills these once it
  //    resolves (Flutter fills route/uom from the fetched value-set when `originalData == null`). ──
  const [dxIds, setDxIds] = useState<string[]>(() => [...(initialDxIds ?? [])]);
  const [note, setNote] = useState(initialDose?.note ?? '');
  const [route, setRoute] = useState(() => initialDose?.route ?? medication.route?.[0]?.code ?? '');
  const [doseUnit, setDoseUnit] = useState(() => initialDose?.doseUnit ?? medication.unitOfMeasurement ?? '');
  const [dispenseUnit, setDispenseUnit] = useState(() => initialDose?.dispenseUnit ?? medication.unitOfMeasurement ?? '');
  const [frequency, setFrequency] = useState<MedFrequency>(() => (initialDose?.frequency as MedFrequency) ?? MED_FREQUENCY.everyDay);
  // FLUTTER'S NEW-PRESCRIPTION DEFAULTS (see numStrOr/today above for the source lines and the
  // display-vs-saved defect this closes). An EDIT still hydrates from the saved dose — the fallback
  // only applies where the wire carried nothing.
  const [quantity, setQuantity] = useState(() => numStrOr(initialDose?.quantity, '1'));
  const [timesADay, setTimesADay] = useState(() => numStrOr(initialDose?.timesADay, '1'));
  const [daysInterval, setDaysInterval] = useState(() => numStrOr(initialDose?.daysInterval, '1'));
  const [specificDays, setSpecificDays] = useState<string[]>(() => initialDose?.specificDays ?? []);
  const [startDate, setStartDate] = useState<Date | undefined>(() => (initialDose?.startDate ? new Date(initialDose.startDate) : today()));
  const [duration, setDuration] = useState(() => numStrOr(initialDose?.duration, '1'));
  const [durationUnit, setDurationUnit] = useState<string>(() => initialDose?.durationUnit ?? DURATION_UNITS[0]);
  // Derived, not a flat 0 — see derivedDispense. Guarded exactly as Flutter guards it: only
  // auto-derive while the dose unit and dispense unit agree.
  const [dispense, setDispense] = useState(() => {
    const saved = numStr(initialDose?.dispense);
    if (saved) return saved;
    const seedDoseUnit = initialDose?.doseUnit ?? medication.unitOfMeasurement ?? '';
    const seedDispenseUnit = initialDose?.dispenseUnit ?? medication.unitOfMeasurement ?? '';
    if (seedDoseUnit !== seedDispenseUnit) return '0';
    /**
     * DERIVE FROM THE SEEDED SCHEDULE, not from 1/1/1.
     *
     * These three were hard-coded, so a seed carrying a real schedule still produced dispense for
     * one dose. Cross-model review (Codex, gpt-5.6-sol) named the case on the ambient path: a
     * dictated "500 mg three times a day for 5 days" seeds `timesADay: 3` and `duration: 5` and
     * every field renders those numbers — while dispense was derived from 1 × 1 × 1 and saved as 1.
     * The clinician sees a correct prescription and the pharmacy gets one tablet.
     *
     * The by-code lookup effect below re-derives dispense when it resolves, which is why this was
     * invisible: on the ordinary path a unit arrives and the recomputation fixes it. With
     * `initialDose` present that effect fills only still-empty fields, so nothing corrected it.
     *
     * Defaults match the state initialisers exactly (`numStrOr(..., '1')`), so a seed that omits a
     * field behaves as it did before.
     */
    const seeded = derivedDispense({
      frequency: (initialDose?.frequency as MedFrequency) ?? MED_FREQUENCY.everyDay,
      timesADay: num(numStrOr(initialDose?.timesADay, '1')),
      quantity: num(numStrOr(initialDose?.quantity, '1')),
      duration: num(numStrOr(initialDose?.duration, '1')),
      durationUnit: initialDose?.durationUnit ?? DURATION_UNITS[0],
      daysInterval: num(numStrOr(initialDose?.daysInterval, '1')),
      specificDaysCount: initialDose?.specificDays?.length ?? 0,
    });
    return seeded == null ? '0' : String(seeded);
  });
  const [refills, setRefills] = useState(() => numStrOr(initialDose?.refill, '0'));
  const [substitution, setSubstitution] = useState(() => initialDose?.substitution ?? false);
  const [sig, setSig] = useState(() => initialDose?.sig ?? '');
  const [weight, setWeight] = useState(() => numStr(initialDose?.weight));
  const [priority, setPriority] = useState<string>(() => initialDose?.priority ?? MED_PRIORITIES[0]);

  // Apply the by-code lookup once it resolves (Flutter `setRouteData`): auto-select the route only when
  // the value-set returns EXACTLY one (>1 → let the user choose), and set dose/dispense units from the
  // fetched UoM. Functional updaters fill ONLY still-empty fields, so this never clobbers a user pick or
  // an edit seed, and needs no field values in its deps.
  useEffect(() => {
    if (!fetchedMed) return;
    const routes = fetchedMed.route ?? [];
    if (routes.length === 1) setRoute((r) => r || routes[0].code);
    const uom = fetchedMed.unitOfMeasurement;
    if (uom) {
      setDoseUnit((u) => u || uom);
      setDispenseUnit((u) => u || uom);
    }
  }, [fetchedMed]);

  /**
   * DISPENSE RECOMPUTE (Andrew, 2026-08-03: "do it"). Flutter re-derives dispense from the schedule on
   * every input change — `setPharmacyNotes()` ends by calling the per-frequency dispense function, and
   * it is wired to ~20 handlers (frequency, the times/units/duration/interval counters, the duration
   * unit, the weekday chips). One effect over the SAME inputs is the React equivalent.
   *
   * `dispense` is deliberately NOT a dependency. That is what preserves a hand-typed value: Flutter
   * recomputes when the OTHER fields move, not when dispense itself does, so a manual figure stands
   * until the schedule changes under it. (It is then overwritten — Flutter tracks no "user touched it"
   * flag, and this is a faithful port of that, not an oversight.)
   *
   * The first run is skipped, matching Flutter's initState: the `originalData` branch does NOT call
   * setInitialDispense/setPharmacyNotes (NMAUW:145-166) — only the new-medication `else` branch does
   * (NMAUW:173-176). So an EDIT keeps its saved dispense until the clinician moves something, and a new
   * prescription already carries the derived seed from useState above.
   */
  const skipFirstDispenseRun = useRef(true);
  useEffect(() => {
    if (skipFirstDispenseRun.current) {
      skipFirstDispenseRun.current = false;
      return;
    }
    // Flutter's own guard: only auto-derive while the two units agree, because once they diverge the
    // arithmetic no longer describes the same thing.
    if (doseUnit !== dispenseUnit) return;
    const next = derivedDispense({
      frequency,
      timesADay: parseNum(timesADay),
      quantity: parseDecimal(quantity),
      duration: parseNum(duration),
      durationUnit,
      daysInterval: parseNum(daysInterval),
      specificDaysCount: specificDays.length,
    });
    if (next != null) setDispense(String(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `dispense` is excluded on purpose (above).
  }, [frequency, timesADay, quantity, duration, durationUnit, daysInterval, specificDays, doseUnit, dispenseUnit]);

  // The unit dropdown options come from the master list; ensure the pre-filled UoM is present
  // (a med's unit may not be in the first page) so it renders selected.
  const unitOptions: SelectOption[] = useMemo(() => {
    const list = unitsQuery.data ?? [];
    const set = new Set(list);
    for (const u of [doseUnit, dispenseUnit, medication.unitOfMeasurement, fetchedMed?.unitOfMeasurement]) if (u) set.add(u);
    return toOptions([...set]);
  }, [unitsQuery.data, doseUnit, dispenseUnit, medication.unitOfMeasurement, fetchedMed?.unitOfMeasurement]);

  // ── Inline drug-interaction check (Flutter getDrugInteractionInfo). Runs on the picked med + the
  //    other plan meds against the patient/diagnoses. Flutter sends the new med with a fixed count of
  //    "1" (not the live dose), so the query is stable on the drug code and doesn't re-fire per dose
  //    keystroke. The response is filtered to alerts triggered by THIS med (Flutter's
  //    `element.id == item.description`). Enabled only when a patient is in context. ──
  //    The check itself lives in `useMedicationInteractionCheck` so an order-set-applied medication
  //    (PROD-2712, AC1) runs exactly this check without the dialog.
  const medSystem = (medication as { system?: string }).system;
  const drugInteractions = useMedicationInteractionCheck({
    medication: { code: medication.code, name: medication.name, system: medSystem },
    patientId,
    providerId,
    diagnoses,
    existingMedications,
  });

  /** The two DOUBLE fields — units per intake and weight. See parseDecimal for why parseInt corrupts. */
  const dec = parseDecimal;
  const durationNum = num(duration);
  const durationNeedsUnit = Number.isFinite(durationNum) && durationNum > 0 && !durationUnit.trim();

  // Occurrence inputs (times-a-day / units-per-intake / start date) apply to the scheduled
  // frequencies (every_day / days_interval / specific_days). "now" needs only weight; "as_needed"
  // needs none. Route + dose unit + dispense(+unit) are always required (PROD-1339).
  const scheduled = frequencyIsScheduled(frequency);
  const dispenseNum = num(dispense);
  // Per-field missing lists (PROD-1588): each banner names ONLY the fields that are actually
  // empty, so a filled dose unit / dispense unit / start date is never called out as missing.
  const baseMissingFields = [
    ...(!route ? ['route'] : []),
    ...(!doseUnit ? ['dose unit'] : []),
    ...(!(dispenseNum > 0) ? ['dispense'] : []),
    ...(!dispenseUnit ? ['dispense unit'] : []),
  ];
  const baseMissing = baseMissingFields.length > 0;
  const scheduleMissingFields = scheduled
    ? [
        ...(!(dec(quantity) > 0) ? ['units per intake'] : []),
        ...(!(num(timesADay) > 0) ? ['times a day'] : []),
        ...(!startDate && !authoring ? ['a start date'] : []),
      ]
    : [];
  const scheduleMissing = scheduleMissingFields.length > 0;
  const intervalMissing = frequency === MED_FREQUENCY.daysInterval && !(num(daysInterval) > 0);
  const daysMissing = frequency === MED_FREQUENCY.specificDays && specificDays.length === 0;
  const nowMissing = !authoring && frequency === MED_FREQUENCY.now && !(dec(weight) > 0);
  // At least one linked diagnosis is REQUIRED — the same rule OrderDialogShell enforces for
  // labs/imaging/procedure, and for the same load-bearing reason: the save mapper drops an order with
  // no dxIds, so letting the save through would silently discard the medication.
  const dxMissing = !authoring && dxIds.length === 0;
  const canConfirm =
    !durationNeedsUnit &&
    !baseMissing &&
    !scheduleMissing &&
    !intervalMissing &&
    !daysMissing &&
    !nowMissing &&
    !dxMissing;

  // ── Forced acknowledgement (patient safety; Flutter `canPop`, new_medication_add_update_widget.dart:1427).
  //    Save on a medication the engine found ANY interaction for (every severity) first asks the
  //    prescriber to acknowledge it: "Acknowledge & Proceed" saves as before, with the interactions on
  //    the order (`vidalAlerts` -> `vidalAlertRequest`); "Don't Prescribe" closes without saving.
  //    While the check is still running, Save waits for it. A failed check does not block Save (the
  //    inline warning shows the error). Authoring an order set has no patient, so no check and no ask.
  //    The alerts asked about are the ones the inline warning shows: triggered BY this drug (Flutter
  //    filters the response to `element.id == item.description`), not deleted.
  const interactionsFound =
    !authoring &&
    (drugInteractions.data ?? []).some((r) => r.isDeleted !== true && (medication.code ? r.id === medication.code : true));
  //    `isFetching`, not `isLoading`: a refetch after a dose or diagnosis change keeps the old data and
  //    does not set `isLoading`, so Save must also wait for it, or it could act on a stale answer.
  const interactionCheckRunning = !authoring && drugInteractions.isFetching;
  const [ackOpen, setAckOpen] = useState(false);

  function toggleDay(day: string, checked: boolean) {
    setSpecificDays((prev) => (checked ? [...prev, day] : prev.filter((d) => d !== day)));
  }

  function handleConfirm() {
    if (!canConfirm || interactionCheckRunning) return;
    if (interactionsFound) {
      setAckOpen(true);
      return;
    }
    save();
  }

  function save() {
    const orderId = `med-${medication.code ?? medication.id ?? medication.name}-${idSuffix}`;
    onConfirm({
      ...medicationResultToOrder(
        // The coding system rides onto the order (PROD-2831): the pick's own (the search row, or the
        // saved order on an edit), else the by-code catalogue row's when it is the SAME code (the
        // lookup can fall back to its first row, which is not proof of identity). Neither has one:
        // the order goes without, as a legacy order already does. Never guessed.
        keepSourceSystem(medication, fetchedMed),
        {
          route,
          routeDisplay: routeOptions.find((o) => o.value === route)?.label,
          doseUnit,
          frequency,
          quantity: Number.isFinite(dec(quantity)) ? dec(quantity) : undefined,
          timesADay: Number.isFinite(num(timesADay)) ? num(timesADay) : undefined,
          daysInterval: Number.isFinite(num(daysInterval)) ? num(daysInterval) : undefined,
          specificDays,
          startDate: startDate && !authoring ? toLocalDateIso(startDate) : undefined,
          duration: Number.isFinite(durationNum) ? durationNum : undefined,
          durationUnit,
          dispense: Number.isFinite(dispenseNum) ? dispenseNum : undefined,
          dispenseUnit,
          refill: Number.isFinite(num(refills)) ? num(refills) : undefined,
          substitution,
          sig,
          // The dialog now HAS a pharmacy-note field, so emit what the clinician typed. (This used to
          // pass `initialDose?.note` straight through, because there was nothing to edit it with.)
          note: authoring ? undefined : note.trim() || undefined,
          // weight/priority are "now"-only (Flutter OtherMedicationInfo) — don't tag every med.
          // CAPTURE the interaction result (Flutter passes `vidalAlertRequest: interactions` on save,
          // new_medication_add_update_widget.dart:1350). The engine already ran for this drug and its
          // answer was thrown away at confirm, so the record never showed that the prescriber was
          // warned and prescribed anyway. Falls back to what is already persisted, so re-confirming a
          // saved med without the engine re-running cannot blank existing alerts (PROD-1801).
          vidalAlerts: authoring ? undefined : (drugInteractions.data as unknown[] | undefined) ?? initialDose?.vidalAlerts,
          weight: !authoring && frequency === MED_FREQUENCY.now && Number.isFinite(dec(weight)) ? dec(weight) : undefined,
          priority: frequency === MED_FREQUENCY.now ? priority : undefined,
        },
        orderId,
      ),
      dxIds,
    });
  }

  /**
   * The sentence the live dialog puts in PHARMACY NOTES, composed from the form — it changes with the
   * frequency: "Take dose immediately" (Now), "Take as needed" (As needed), else
   * "Take {n} {unit}, {t} time(s) a day, for {d} {durUnit}, starting {date}". Used as the placeholder
   * so it never silently overwrites something a clinician typed.
   */
  const pharmacyNotesHint = (() => {
    if (frequency === MED_FREQUENCY.now) return 'Take dose immediately';
    if (frequency === MED_FREQUENCY.asNeeded) return 'Take as needed';
    const parts: string[] = [];
    const qty = dec(quantity);
    parts.push(`Take ${Number.isFinite(qty) && qty > 0 ? qty : 1}${doseUnit ? ` ${doseUnit}` : ''}`);
    const times = num(timesADay);
    const t = Number.isFinite(times) && times > 0 ? times : 1;
    parts.push(`${t} time${t === 1 ? '' : 's'} a day`);
    const dur = num(duration);
    // Pluralise the duration unit — the render read "for 7 day". `day` becomes `days`; the wk/mo
    // abbreviations pluralise the same way (wks / mos), which is how the live dialog abbreviates them.
    if (Number.isFinite(dur) && dur > 0) {
      parts.push(`for ${dur} ${durationUnit}${dur === 1 ? '' : 's'}`);
    }
    if (startDate && !authoring) {
      parts.push(
        `starting ${startDate.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}`,
      );
    }
    return parts.join(', ');
  })();

  const medLabel = medication.strength ? `${medication.name} ${medication.strength}` : medication.name;

  return (
    // NO MODAL TITLE — see OrderDialogShell. The frame opens on the MEDICATION field label.
    <Modal open onClose={onCancel} size="lg">
      <div
        className={`${BODY} ${FLUTTER_36} ${AP_DIALOG_LABELS}`}
        role="group"
        aria-label={editing ? 'Edit medication' : 'Add medication'}
        data-slot="med-dose"
      >
        <style>{FLUTTER_36_CSS}</style>
        {/*
          FIELD ORDER IS THE DESIGN'S, not ours (PROD-1801; all five frequency states captured from
          the live dialog 2026-07-30). The previous order — Route, Frequency, Dose unit, Dispense
          unit, Dispense, Refills, then one scheduled block — was invented. The real sequence:
            MEDICATIONS header, DIAGNOSIS, FREQUENCY
            [Days interval only]  EVERY 'NUMBER' OF DAYS
            [Specific days only]  ON WHICH DAYS  (Mo Tu Wd Th Fr Sa Su)
            TIMES A DAY | UNITS PER INTAKE        (scheduled frequencies only)
            ROUTE | UNIT
            [Now only]  WEIGHT | PRIORITY
            START DATE | DURATION (+ day/wk/mo links)   (scheduled only)
            DISPENSE | DISPENSE UNIT
            REFILLS
            Allow Substitution (switch), PHARMACY NOTES
          Now and As-needed drop times/units/start/duration entirely; only Now shows weight+priority.
        */}
        <div>
          <span className={MED_FIELD_LABEL}>Medications</span>
          <div className={MED_NAME} data-slot="med-dose-name">{medLabel}</div>
          {medication.code ? <div className={MED_CODE} data-slot="med-dose-code">{medication.code}</div> : null}
        </div>

        {/* R1 limit, visible (PROD-2721 AC1): no medication safety engine runs today, so every
            single order says so. Not shown while authoring an order set (nothing is ordered). */}
        {!authoring && <NoMedicationChecksNotice />}

        {/* Inline drug-interaction warning (Flutter DrugInteractionInfoWidget) — only when a patient
            is in context. Shows the engine's alerts for the picked med, or a failure + Retry; never a
            "no interaction" all-clear (PROD-2721). */}
        {!!patientId && (
          <DrugInteractionWarning
            interactions={drugInteractions.data}
            loading={drugInteractions.isLoading}
            error={drugInteractions.isError}
            onRetry={() => void drugInteractions.refetch()}
            triggerCode={medication.code}
          />
        )}

        {/* DIAGNOSIS (n) * — between the header and FREQUENCY, exactly as the live dialog places it.
            An order with no link is dropped by the save mapper, so Save is gated on it below. */}
        {!authoring && <DiagnosisMultiSelect diagnoses={dxOptions} selectedIds={dxIds} onChange={setDxIds} />}

        {/* THE TRANSCRIPT DISAGREES WITH THE MODEL'S SCHEDULE (PROD-2018).
            Rendered ABOVE Frequency because it is about the field directly beneath it, and a
            clinician who reads nothing else on this dialog reads the field they are about to set.

            This exists because withholding the schedule was NOT enough. `frequency` below defaults
            an absent value to `everyday` / `timesADay: 1`, so a cleared twice-weekly regimen
            silently became seven doses a week on a form that looked complete. Cross-model review
            found it. A withheld value is indistinguishable from one nobody dictated — only a
            message carries the difference, so the message is not optional. */}
        {initialDose?.contradiction ? (
          <div role="alert" className={CONTRADICTION_BANNER} data-slot="med-dose-warn">
            <strong>Check the schedule.</strong> {initialDose.contradiction}
          </div>
        ) : null}

        {/* WHERE THE FILLED-IN VALUES CAME FROM (`frameV2`, 1 Oct). Set whenever a dictated sig rode
            onto the catalogue row the clinician picked (`dictated-dose-for-pick.ts`). Neither a name
            match nor a search match proves it is the same drug (a combination product matches
            both), so the physician is told what the words were spoken for and checks them against
            what she picked. Absent on every other path, so the dialog is otherwise unchanged. */}
        {initialDose?.dictatedFor ? (
          <div role="status" aria-label="Filled in from the dictation" className={CONTRADICTION_BANNER} data-slot="med-dose-warn">
            <strong>Check the dose.</strong> Filled in from what was dictated for “{initialDose.dictatedFor}”.
            Check it is right for {medication.name} before you save.
          </div>
        ) : null}

        {/* FREQUENCY — a segmented pill row, first after the header. Spans full width and scrolls:
            five pills are ~500px and inside a grid column they forced the card wider than the modal. */}
        <div className={FREQUENCY_ROW}>
          <span className={MED_FIELD_LABEL}>Frequency</span>
          <Segmented
            // `Now` is hidden (Andrew, 30 Jul). But a medication SAVED earlier can still carry it,
            // and a Segmented whose value isn't in its options renders nothing selected — the
            // clinician would see a frequency-less form and silently re-save it wrong. So a legacy
            // `now` order keeps its pill visible; only new orders can't pick it.
            options={[...(frequency === MED_FREQUENCY.now ? MED_FREQUENCY_ALL : MED_FREQUENCY_OPTIONS)]}
            value={frequency}
            onChange={(v) => setFrequency(v as MedFrequency)}
          />
        </div>

        {/* Days-interval gets its own full-width row ABOVE times/units, and the live dialog labels it
            EVERY 'NUMBER' OF DAYS — not "Every N days". */}
        {frequency === MED_FREQUENCY.daysInterval && (
          <NumberSelector
            label={DAYS_INTERVAL_LABEL}
            required
            min={1}
            max={365}
            value={num(daysInterval) || 1}
            onChange={(n) => setDaysInterval(String(n))}
          />
        )}

        {/* Specific days — two-letter chips, not full weekday names. */}
        {frequency === MED_FREQUENCY.specificDays && (
          <div>
            <span className={MED_FIELD_LABEL}>On which days</span>
            <div className={DAYS}>
              {WEEKDAYS.map((day) => {
                const on = specificDays.includes(day);
                return (
                  <Button
                    key={day}
                    variant={on ? 'primary' : 'secondary'}
                    size="sm"
                    aria-pressed={on}
                    aria-label={day}
                    onClick={() => toggleDay(day, !on)}
                  >
                    {WEEKDAY_SHORT[day] ?? day.slice(0, 2)}
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {/* Times a day | Units per intake — scheduled only (absent for Now / As needed). */}
        {scheduled && (
          <DoseGrid>
            <NumberSelector label="Times a day" required min={1} max={24} value={num(timesADay) || 1} onChange={(n) => setTimesADay(String(n))} />
            {/*
              A DECIMAL counter, matching Flutter: quantity is a double stepped by 0.25 from 0
              (NMAUW:751,758,765). `step` is what puts the DS NumberSelector into decimal mode
              (`mode ?? (step % 1 === 0 ? 'integer' : 'decimal')`), and `decimals={2}` renders 0.25
              rather than truncating the display.

              `min={0}` is Flutter's `max(0, …)`, not min 1 — a 0 is reachable there and the required
              gate below already blocks saving one, so clamping to 1 here would only hide it.

              NOTE the value expression: `Number.isFinite(q) ? q : 1`, NOT the old `dec(quantity) || 1`.
              `|| 1` renders a legitimate 0 as 1 — the exact display-lie that let the parseInt bug
              hide, since the form showed a dose the record did not hold.
            */}
            <NumberSelector
              label="Units per intake"
              required
              min={0}
              max={99}
              step={QUANTITY_STEP}
              decimals={2}
              value={Number.isFinite(dec(quantity)) ? dec(quantity) : 1}
              onChange={(n) => setQuantity(String(n))}
            />
          </DoseGrid>
        )}

        <DoseGrid>
          <Select size="compact" label="Route" required options={routeOptions} value={route} onChange={setRoute} placeholder="Select route" error={routeError} />
          {/* The live dialog labels the dose unit simply UNIT. */}
          <Select size="compact" label="Unit" required options={unitOptions} value={doseUnit} onChange={setDoseUnit} placeholder="Select unit" error={unitsError} />
        </DoseGrid>

        {/* Weight + Priority — ONLY for an immediate ("Now") order. */}
        {frequency === MED_FREQUENCY.now && (
          <DoseGrid>
            {!authoring && (
              <TextField size="compact" label="Weight" required type="number" inputMode="decimal" min={0} value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="0" />
            )}
            <Select size="compact" label="Priority" options={toOptions([...MED_PRIORITIES])} value={priority} onChange={setPriority} placeholder="Select priority" />
          </DoseGrid>
        )}

        {/* Start date | Duration — scheduled only. DURATION carries day/wk/mo LINKS in the counter's
            unit slot, not a separate dropdown: that is the anatomy the frame and Flutter both show. */}
        {scheduled && (
          <DoseGrid>
            {!authoring && <DatePicker label="Start date" required value={startDate} onChange={setStartDate} />}
            <NumberSelector
              label="Duration"
              min={0}
              max={365}
              value={num(duration) || 0}
              onChange={(n) => setDuration(String(n))}
              unit={
                <span className={DURATION_LINKS} data-slot="med-dose-duration">
                  {DURATION_UNITS.map((u) => (
                    <button
                      key={u}
                      type="button"
                      aria-pressed={u === durationUnit}
                      onClick={() => setDurationUnit(u)}
                      className={u === durationUnit ? DURATION_LINK_ON : DURATION_LINK_OFF}
                    >
                      {u}
                    </button>
                  ))}
                </span>
              }
            />
          </DoseGrid>
        )}

        <DoseGrid>
          <NumberSelector
            label={dispenseUnit ? `Dispense (${dispenseUnit})` : 'Dispense'}
            required
            min={0}
            max={999}
            value={num(dispense) || 0}
            onChange={(n) => setDispense(String(n))}
          />
          <Select size="compact" label="Dispense unit" required options={unitOptions} value={dispenseUnit} onChange={setDispenseUnit} placeholder="Select unit" error={unitsError} />
        </DoseGrid>

        <NumberSelector label="Refills" min={0} max={12} value={num(refills) || 0} onChange={(n) => setRefills(String(n))} />

        {/* Allow Substitution is a SWITCH with a sentence-case label on the left — not a checkbox,
            and not an uppercase field label. */}
        <div className={SUBSTITUTION_ROW}>
          <span className={SUBSTITUTION_LABEL} data-slot="med-dose-sub-label">Allow Substitution</span>
          <Switch checked={substitution} onCheckedChange={setSubstitution} aria-label="Allow Substitution" />
        </div>

        {/*
          SIG (one line) and PHARMACY NOTES (multi-line) are TWO SEPARATE FIELDS — Andrew's ruling,
          30 Jul, and the frame shows both.

          I had flagged this as blocked on "which one reaches the wire". THAT WAS WRONG, and checking
          instead of assuming settled it in a minute: the medication wire already carries BOTH, and
          both round-trip. `sig` → `sig` (to-patient-assessment.ts:289, restored at
          from-patient-assessment.ts:230); the note → `notes` + `notePlainText` (…:291-292, restored
          from `med.notes`). Nothing is discarded and no model change was needed.

          SIG is the dosing instruction that prints on the label; PHARMACY NOTES is free text for the
          pharmacist. The generated sentence stays on SIG as its placeholder, because that is the
          field it describes.
        */}
        <TextField size="compact"
          label="Sig"
          value={sig}
          onChange={(e) => setSig(e.target.value.slice(0, 300))}
          placeholder={pharmacyNotesHint}
        />

        {!authoring && (
          <TextArea
            label="Pharmacy notes"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 300))}
            placeholder="Notes for pharmacy related to, but NOT part of SIG"
            rows={3}
            maxLength={300}
          />
        )}

        {baseMissing && <HelperText tone="error">{requiredMessage(baseMissingFields)}</HelperText>}
        {scheduleMissing && (
          <HelperText tone="error">{requiredMessage(scheduleMissingFields, ' for this frequency')}</HelperText>
        )}
        {daysMissing && <HelperText tone="error">Select at least one day.</HelperText>}
        {nowMissing && <HelperText tone="error">Weight is required for an immediate (now) order.</HelperText>}
        {durationNeedsUnit && <HelperText tone="error">Add a duration unit — or clear the duration.</HelperText>}

        {/* Save LEFT of Cancel and the pair LEFT-aligned, with the destructive delete opposite —
            identical to OrderDialogShell, because both Figma frames (`1736:158994` medication,
            `1736:159053` radiology) draw the same footer. This row was right-aligned and had no
            delete; the five order dialogs now share one footer layout instead of two. */}
        <div className={ACTIONS} data-slot="med-dose-actions">
          <div className={ACTIONS_MAIN}>
            <Button size="sm" onClick={handleConfirm} disabled={!canConfirm || interactionCheckRunning}>Save</Button>
            <Button variant="tertiary" size="sm" onClick={onCancel}>Cancel</Button>
          </div>
          {onDelete && (
            <button type="button" aria-label="Delete medication" className={DELETE_BUTTON} onClick={onDelete}>
              <Trash2 className="size-4" aria-hidden />
            </button>
          )}
        </div>
      </div>
      <DrugInteractionAcknowledgeDialog
        open={ackOpen}
        onProceed={() => {
          setAckOpen(false);
          save();
        }}
        onDecline={() => {
          setAckOpen(false);
          onCancel();
        }}
      />
    </Modal>
  );
}

/**
 * Exposed for tests. `derivedDispense` carries the pharmacy arithmetic — including Flutter’s NESTED
 * ceil in the interval/specific-days cases — so it is pinned directly rather than only through the UI,
 * where a wrong formula could hide behind a passing click-through.
 */
export const __testables = { derivedDispense };
